/**
 * Live theme state (dark mode, KAN-304 step 4). One module-level controller:
 *   - the signed-in user's preference ('system' | 'light' | 'dark'), copied to the device so the next
 *     first paint is right (themeMode.js, key `cvx:theme-pref`);
 *   - the OS appearance, when the preference is 'system' (re-applied live on a `change`);
 *   - a ref count of forced-Light customer pages (useForceLightTheme). When the last one lets go the
 *     user's REAL theme comes back, never a value captured earlier.
 * Nothing here reads the account; ThemePreferenceSync does, once per sign-in.
 */

import { useSyncExternalStore } from 'react';
import {
  resolveTheme, applyTheme, readDevicePref, writeDevicePref, clearDevicePrefOwner, validPref,
} from './themeMode';
import { runThemeSwitch } from './themeSwitchMotion';
import './accessibilityModes.css';
import './raisedLayers.css';

let pref = 'system';
let forcedCount = 0;
let changeCount = 0;
let snapshot = { pref, theme: 'light' };
let mediaBound = false;
const listeners = new Set();

function compute() {
  if (forcedCount > 0) return 'light';
  return resolveTheme({ devicePref: pref });
}

/** `animate`: a LIVE change cross-fades (themeSwitchMotion); boot and same-theme refreshes apply in place. */
function refresh(animate = true) {
  const theme = compute();
  let shown = null;
  try { shown = document.documentElement.dataset.theme || null; } catch { shown = null; }
  if (animate && shown && shown !== theme) runThemeSwitch(() => applyTheme(theme));
  else applyTheme(theme);
  if (snapshot.pref !== pref || snapshot.theme !== theme) {
    snapshot = { pref, theme };
    listeners.forEach((l) => l());
  }
}

function bindMedia() {
  if (mediaBound || typeof window === 'undefined') return;
  let mq = null;
  try { mq = window.matchMedia('(prefers-color-scheme: dark)'); } catch { mq = null; }
  if (!mq) return;
  mediaBound = true;
  const onChange = () => { if (pref === 'system') refresh(); };
  if (mq.addEventListener) mq.addEventListener('change', onChange);
  else if (mq.addListener) mq.addListener(onChange);
}

/** Boot (src/index.js): load the device copy, apply the theme, start following the OS. */
export function initThemeController() {
  pref = readDevicePref() || 'system';
  bindMedia();
  refresh(false);
  return snapshot.theme;
}

export function getThemePreference() { return pref; }
/** Bumps on every setThemePreference: lets a slow account read notice the user already chose. */
export function getThemeChangeCount() { return changeCount; }
export function getResolvedTheme() { return snapshot.theme; }

/** Applies a preference at once and keeps the device copy (+ the id of the user it belongs to). Anything but system|light|dark is ignored. */
export function setThemePreference(next, ownerId) {
  const v = validPref(next);
  if (!v) return;
  pref = v;
  changeCount += 1;
  writeDevicePref(v, undefined, ownerId);
  bindMedia();
  refresh();
}

/**
 * Sign-out (every path, incl. expiry / revoke): keep the theme so the sign-in screen and the next sign-in's splash
 * stay in the same look (no switch at sign-in), but forget WHO owned it — the owner id is per-user device state.
 */
export function forgetThemeOwnerForSignOut() {
  clearDevicePrefOwner();
}

/** Pins Light (customer pages). Returns an idempotent release; the last release restores the user's theme. */
export function pushForcedLightTheme() {
  forcedCount += 1;
  refresh();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    forcedCount = Math.max(0, forcedCount - 1);
    refresh();
  };
}

function subscribe(l) { listeners.add(l); return () => listeners.delete(l); }
const getSnapshot = () => snapshot;

/** { pref, theme } — re-renders on any change. */
export function useThemePreference() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Tests only. */
export function __resetThemeController() {
  pref = 'system';
  forcedCount = 0;
  changeCount = 0;
  mediaBound = false;
  snapshot = { pref, theme: 'light' };
  listeners.clear();
}
